import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Server,
  Users,
  User,
  GitBranch,
  Target,
  Waypoints,
  FileText,
  Scale,
  Boxes,
  AlertTriangle,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  EmptyState,
  PageHeader,
  Spinner,
  useToast,
} from '../../ui';
import { useServices } from '../../features/services/useServices';
import { deriveServiceExperts } from '../../features/services/serviceModel';
import type { Service } from '../../features/services/types';
import { ServiceModal } from './ServiceModal';

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: typeof Target;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-zinc-100">
        {Icon && <Icon className="h-4 w-4 text-accent" aria-hidden="true" />}
        {title}
      </h2>
      {children}
    </div>
  );
}

function LinkRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full truncate rounded-lg px-2 py-1.5 text-left text-sm text-zinc-300 transition-colors hover:bg-surface-2 hover:text-white"
    >
      {label}
    </button>
  );
}

/** Service detail — start here: who owns it, who knows it, and everything linked. */
export function ServiceDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    services,
    goals,
    workstreams,
    documents,
    decisions,
    currentAlias,
    goalTitles,
    workstreamTitles,
    documentTitles,
    decisionTitles,
    loading,
    error,
    get,
    update,
    remove,
  } = useServices();

  const service = useMemo(() => services.find((s) => s.id === id), [services, id]);
  const experts = useMemo(
    () => (service ? deriveServiceExperts(service, documents, decisions) : []),
    [service, documents, decisions],
  );

  const [editing, setEditing] = useState<Service | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openEdit = async () => {
    const full = await get(id);
    if (!full) {
      toast({ tone: 'error', title: 'Not found', description: 'Service could not be loaded.' });
      return;
    }
    setEditing(full);
    setModalOpen(true);
  };

  const onSubmit = async (draft: Parameters<typeof update>[1]) => {
    await update(id, draft);
    toast({ tone: 'success', title: 'Service updated', description: draft.name });
  };
  const doDelete = async () => {
    if (!service) return;
    setDeleting(true);
    try {
      await remove(service.id);
      toast({ tone: 'success', title: 'Service removed', description: service.name });
      navigate('/services');
    } catch (e) {
      toast({
        tone: 'error',
        title: 'Could not remove service',
        description: e instanceof Error ? e.message : 'Delete failed.',
      });
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner label="Loading service" />
      </div>
    );
  }
  if (error) {
    return <EmptyState icon={<AlertTriangle className="h-6 w-6" />} title="Couldn't load service" description={error} />;
  }
  if (!service) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6" />}
        title="Service not found"
        description="It may have been removed."
        action={<Button onClick={() => navigate('/services')}>Back to Service Directory</Button>}
      />
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate('/services')}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition-colors hover:text-zinc-200"
      >
        <ArrowLeft className="h-4 w-4" /> Service Directory
      </button>

      <PageHeader
        icon={<Server className="h-5 w-5" />}
        title={service.name || 'Unnamed service'}
        description={service.purpose}
        actions={
          <>
            <Button variant="outline" leftIcon={<Pencil className="h-4 w-4" />} onClick={openEdit}>
              Edit
            </Button>
            <Button variant="ghost" leftIcon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmingDelete(true)}>
              Delete
            </Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Who knows this system. */}
          <Card>
            <CardBody>
              <Section title="Who knows this system?" icon={Users}>
                {experts.length === 0 ? (
                  <p className="text-sm text-zinc-500">
                    No experts derived yet — add a POC, or link documents / decisions.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {experts.map((e) => (
                      <div
                        key={`${e.isTeam ? 'team' : 'user'}:${e.who}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-surface-border bg-surface-1 px-3 py-2"
                      >
                        <span className="inline-flex items-center gap-2 text-sm text-zinc-200">
                          {e.isTeam ? (
                            <Boxes className="h-4 w-4 text-zinc-400" aria-hidden="true" />
                          ) : (
                            <User className="h-4 w-4 text-zinc-400" aria-hidden="true" />
                          )}
                          {e.who}
                        </span>
                        <span className="flex flex-wrap justify-end gap-1">
                          {e.roles.map((r) => (
                            <Badge key={r} tone="neutral">{r}</Badge>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </CardBody>
          </Card>

          {/* Connections. */}
          <Card>
            <CardBody className="space-y-4">
              {service.currentGoalIds.length > 0 && (
                <Section title="Current goals" icon={Target}>
                  <div className="space-y-0.5">
                    {service.currentGoalIds.map((g) => (
                      <LinkRow key={g} label={goalTitles.get(g) ?? g} onClick={() => navigate(`/goals/${encodeURIComponent(g)}`)} />
                    ))}
                  </div>
                </Section>
              )}
              {service.activeWorkstreamIds.length > 0 && (
                <Section title="Active workstreams" icon={Waypoints}>
                  <div className="space-y-0.5">
                    {service.activeWorkstreamIds.map((w) => (
                      <LinkRow key={w} label={workstreamTitles.get(w) ?? w} onClick={() => navigate(`/workstreams/${encodeURIComponent(w)}`)} />
                    ))}
                  </div>
                </Section>
              )}
              {service.documentIds.length > 0 && (
                <Section title="Documents" icon={FileText}>
                  <div className="space-y-0.5">
                    {service.documentIds.map((d) => (
                      <LinkRow key={d} label={documentTitles.get(d) ?? d} onClick={() => navigate(`/documents/${encodeURIComponent(d)}`)} />
                    ))}
                  </div>
                </Section>
              )}
              {service.recentDecisionIds.length > 0 && (
                <Section title="Recent decisions" icon={Scale}>
                  <div className="space-y-0.5">
                    {service.recentDecisionIds.map((d) => (
                      <LinkRow key={d} label={decisionTitles.get(d) ?? d} onClick={() => navigate(`/decisions/${encodeURIComponent(d)}`)} />
                    ))}
                  </div>
                </Section>
              )}
              {service.currentGoalIds.length === 0 &&
                service.activeWorkstreamIds.length === 0 &&
                service.documentIds.length === 0 &&
                service.recentDecisionIds.length === 0 && (
                  <p className="text-sm text-zinc-500">
                    Nothing linked yet — connect goals, workstreams, docs and decisions from the edit form.
                  </p>
                )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardBody className="space-y-3 text-sm">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Owning team</p>
                <p className="text-zinc-200">{service.owningTeam || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Primary POC</p>
                <p className="text-zinc-200">{service.primaryPoc || '—'}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Secondary POC</p>
                <p className="text-zinc-200">{service.secondaryPoc || '—'}</p>
              </div>
              {service.repositories.length > 0 && (
                <div>
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                    <GitBranch className="h-3 w-3" /> Repositories
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {service.repositories.map((r) => (
                      <Badge key={r} tone="neutral">{r}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {service.knownDependencies.length > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Known dependencies</p>
                  <div className="flex flex-wrap gap-1.5">
                    {service.knownDependencies.map((d) => (
                      <Badge key={d} tone="neutral">{d}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate(`/timeline?scope=service&id=${encodeURIComponent(service.id)}`)}
          >
            View timeline
          </Button>
        </div>
      </div>

      <ServiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultPoc={currentAlias}
        initialService={editing}
        goals={goals}
        workstreams={workstreams}
        documents={documents}
        decisions={decisions}
        goalTitles={goalTitles}
        workstreamTitles={workstreamTitles}
        documentTitles={documentTitles}
        decisionTitles={decisionTitles}
        onSubmit={onSubmit}
      />

      <ConfirmDialog
        open={confirmingDelete}
        title="Remove service?"
        confirmLabel="Remove service"
        loading={deleting}
        onConfirm={() => void doDelete()}
        onClose={() => setConfirmingDelete(false)}
        message="Remove this service from the directory? This can't be undone."
      />
    </div>
  );
}

export default ServiceDetailPage;
